import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UpdateAgentData, AgentContact } from '../../api/modules/agentApi'
import { apiClient } from '../../api'

const AGENT_QUERY_KEY = (id: string) => ['agent', id]
const AGENTS_QUERY_KEY = ['agents']
const AGENT_CONTACTS_QUERY_KEY = (id: string) => ['agentContacts', id]

export const useAgent = (agentId: string) => {
  const queryClient = useQueryClient()

  // Query for fetching individual agent
  const {
    data: agent,
    isLoading: isLoadingAgent,
    error: agentError,
    refetch: refetchAgent,
  } = useQuery({
    queryKey: AGENT_QUERY_KEY(agentId),
    queryFn: () => apiClient.agent.fetchAgent(agentId),
    enabled: !!agentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  })

  // Mutation for updating an agent
  const updateAgentMutation = useMutation({
    mutationFn: (data: UpdateAgentData) => apiClient.agent.updateAgent(data),
    onSuccess: updatedAgent => {
      // Update the individual agent cache
      queryClient.setQueryData(AGENT_QUERY_KEY(agentId), updatedAgent)
      // Invalidate the agents list to refresh it
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY })
    },
  })

  // Mutation for deleting an agent
  const deleteAgentMutation = useMutation({
    mutationFn: (id: string) => apiClient.agent.deleteAgent(id),
    onSuccess: () => {
      // Remove the individual agent from cache
      queryClient.removeQueries({ queryKey: AGENT_QUERY_KEY(agentId) })
      // Invalidate the agents list to refresh it
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY })
    },
  })

  // Query for fetching agent contacts
  const {
    data: contactsData,
    isLoading: isLoadingContacts,
    error: contactsError,
    refetch: refetchContacts,
  } = useQuery({
    queryKey: AGENT_CONTACTS_QUERY_KEY(agentId),
    queryFn: () => apiClient.agent.fetchAgentContacts(agentId),
    enabled: !!agentId,
    staleTime: 60 * 60 * 1000, // 60 minutes - contacts rarely change
    gcTime: 60 * 60 * 1000, // Keep in cache for 60 minutes
    retry: 2,
  })

  // Mutation for creating a contact
  const createContactMutation = useMutation({
    mutationFn: (contact: AgentContact) => apiClient.agent.createAgentContact(agentId, contact),
    onSuccess: () => {
      // Invalidate the contacts list to refresh it
      queryClient.invalidateQueries({ queryKey: AGENT_CONTACTS_QUERY_KEY(agentId) })
    },
  })

  // Mutation for updating a contact
  const updateContactMutation = useMutation({
    mutationFn: ({ country, contact }: { country: string; contact: AgentContact }) =>
      apiClient.agent.updateAgentContact(agentId, country, contact),
    onSuccess: () => {
      // Invalidate the contacts list to refresh it
      queryClient.invalidateQueries({ queryKey: AGENT_CONTACTS_QUERY_KEY(agentId) })
    },
  })

  // Mutation for deleting a contact
  const deleteContactMutation = useMutation({
    mutationFn: (country: string) => apiClient.agent.deleteAgentContact(agentId, country),
    onSuccess: () => {
      // Invalidate the contacts list to refresh it
      queryClient.invalidateQueries({ queryKey: AGENT_CONTACTS_QUERY_KEY(agentId) })
    },
  })

  return {
    // Agent Data
    agent,
    isLoadingAgent,
    agentError: agentError?.message || null,

    // Agent Actions
    refetchAgent,
    updateAgent: updateAgentMutation.mutateAsync,
    deleteAgent: deleteAgentMutation.mutateAsync,

    // Agent Mutation states
    isUpdatingAgent: updateAgentMutation.isPending,
    isDeletingAgent: deleteAgentMutation.isPending,

    // Agent Mutation errors
    updateAgentError: updateAgentMutation.error?.message || null,
    deleteAgentError: deleteAgentMutation.error?.message || null,

    // Contact Data
    contactsData,
    isLoadingContacts,
    contactsError: contactsError?.message || null,

    // Contact Actions
    refetchContacts,
    createContact: createContactMutation.mutateAsync,
    updateContact: updateContactMutation.mutateAsync,
    deleteContact: deleteContactMutation.mutateAsync,

    // Contact Mutation states
    isCreatingContact: createContactMutation.isPending,
    isUpdatingContact: updateContactMutation.isPending,
    isDeletingContact: deleteContactMutation.isPending,

    // Contact Mutation errors
    createContactError: createContactMutation.error?.message || null,
    updateContactError: updateContactMutation.error?.message || null,
    deleteContactError: deleteContactMutation.error?.message || null,
  }
}

export default useAgent
